import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeftRight, Loader2 } from 'lucide-react';
import ClientSelectDropdown from './ClientSelectDropdown';
import biApi from '@/services/biApi';

interface CloneSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type CloneOption = 'prompts' | 'structure' | 'menus';

const CLONE_OPTIONS: Array<{ value: CloneOption; label: string }> = [
  { value: 'prompts', label: 'Prompts de IA' },
  { value: 'structure', label: 'Estrutura Organizacional' },
  { value: 'menus', label: 'Menus Interativos' },
];

const CloneSettingsModal: React.FC<CloneSettingsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { toast } = useToast();
  const [sourceClientId, setSourceClientId] = useState<number | undefined>(undefined);
  const [targetClientId, setTargetClientId] = useState<number | undefined>(undefined);
  const [selectedOptions, setSelectedOptions] = useState<CloneOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleSwap = () => {
    const temp = sourceClientId;
    setSourceClientId(targetClientId);
    setTargetClientId(temp);
  };

  const handleOptionToggle = (option: CloneOption) => {
    console.log(`🔧 [CloneSettingsModal] Toggle opção: ${option}`);
    setSelectedOptions((prev) => {
      const newOptions = prev.includes(option)
        ? prev.filter((o) => o !== option)
        : [...prev, option];
      console.log(`🔧 [CloneSettingsModal] Opções selecionadas:`, newOptions);
      return newOptions;
    });
  };

  const handleClone = async () => {
    // Validações
    if (!sourceClientId || !targetClientId) {
      toast({
        title: 'Erro',
        description: 'Selecione os clientes de origem e destino',
        variant: 'destructive',
      });
      return;
    }

    if (sourceClientId === targetClientId) {
      toast({
        title: 'Erro',
        description: 'Os clientes de origem e destino devem ser diferentes',
        variant: 'destructive',
      });
      return;
    }

    if (selectedOptions.length === 0) {
      toast({
        title: 'Erro',
        description: 'Selecione pelo menos uma configuração para clonar',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await biApi.cloneSettings(
        sourceClientId,
        targetClientId,
        selectedOptions
      );

      toast({
        title: 'Sucesso',
        description: `Configurações clonadas com sucesso! ${result.summary || ''}`,
      });

      // Resetar formulário
      setSourceClientId(undefined);
      setTargetClientId(undefined);
      setSelectedOptions([]);

      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Erro ao clonar configurações:', error);
      toast({
        title: 'Erro',
        description: error?.message || 'Não foi possível clonar as configurações',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    setSourceClientId(undefined);
    setTargetClientId(undefined);
    setSelectedOptions([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Clonar Configurações de Atendimentos IA</DialogTitle>
          <DialogDescription>
            Selecione o cliente de origem e destino para clonar as configurações
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Seleção de Clientes - Estilo GitHub */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleSwap}
                className="h-9 w-9 shrink-0"
                disabled={!sourceClientId || !targetClientId || isLoading}
              >
                <ArrowLeftRight className="h-4 w-4" />
              </Button>

              <div className="flex-1 grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                    base:
                  </Label>
                  <ClientSelectDropdown
                    value={sourceClientId}
                    onValueChange={setSourceClientId}
                    placeholder="Cliente Origem"
                    hideLabel={true}
                    showAllOption={false}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                    compare:
                  </Label>
                  <ClientSelectDropdown
                    value={targetClientId}
                    onValueChange={setTargetClientId}
                    placeholder="Cliente Destino"
                    hideLabel={true}
                    showAllOption={false}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Checkboxes de Configurações */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-900">
              Configurações a Clonar:
            </Label>
            <div className="space-y-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
              {CLONE_OPTIONS.map((option) => {
                const isChecked = selectedOptions.includes(option.value);
                return (
                  <label
                    key={option.value}
                    htmlFor={option.value}
                    className="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 p-2 rounded transition-colors"
                  >
                    <Checkbox
                      id={option.value}
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        console.log(`🔧 [CloneSettingsModal] Checkbox ${option.value} alterado para:`, checked);
                        console.log(`🔧 [CloneSettingsModal] Estado atual antes:`, selectedOptions);
                        handleOptionToggle(option.value);
                      }}
                      disabled={isLoading}
                      onClick={(e) => {
                        // Prevenir que o evento seja tratado duas vezes
                        e.stopPropagation();
                      }}
                    />
                    <span className="text-sm font-normal flex-1 select-none">
                      {option.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer com Botões */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleClone}
            disabled={isLoading || !sourceClientId || !targetClientId || selectedOptions.length === 0}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Clonando...
              </>
            ) : (
              'Clonar Configurações'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CloneSettingsModal;

